/**
 * AdminHub — v109.9.53
 *
 * Master-admin-only utility hub. No more module tiles. Four inner tabs only:
 *
 *   1. Master Access  — RBAC editor (roles, grants, common, matrix)
 *   2. Backup & Restore — Mongo backup/restore panel
 *   3. Chat            — full-featured internal chat
 *   4. Recycle Bin     — cross-app soft-delete restore
 *
 * All four are embedded in-place so master admin never has to navigate away.
 */
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, KeyRound, Database, MessageSquare, Trash2 } from "lucide-react";
import { PageHeader } from "../components/GradientCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

import MasterAccess from "./MasterAccess";
import BackupRestore from "./BackupRestore";
import Chat from "./Chat";
import RecycleBin from "./RecycleBin";

export default function AdminHub() {
  const { user } = useAuth();
  if (user?.role !== "master_admin") return <Navigate to="/" replace />;

  return (
    <div className="max-w-[1500px] space-y-6" data-testid="admin-hub-page">
      <PageHeader
        tone="violet"
        icon={ShieldCheck}
        eyebrow="Master Admin"
        title="Admin Hub"
        subtitle="Everything that requires the Master Admin — access grants, backups, cross-team chat, and the recycle bin — in one place."
      />

      <Tabs defaultValue="access" className="w-full">
        <TabsList className="flex flex-wrap h-auto" data-testid="admin-hub-tabs">
          <TabsTrigger value="access" data-testid="ah-tab-access">
            <KeyRound size={14} className="mr-1.5" /> Master Access
          </TabsTrigger>
          <TabsTrigger value="backup" data-testid="ah-tab-backup">
            <Database size={14} className="mr-1.5" /> Backup &amp; Restore
          </TabsTrigger>
          <TabsTrigger value="chat" data-testid="ah-tab-chat">
            <MessageSquare size={14} className="mr-1.5" /> Chat
          </TabsTrigger>
          <TabsTrigger value="recycle" data-testid="ah-tab-recycle">
            <Trash2 size={14} className="mr-1.5" /> Recycle Bin
          </TabsTrigger>
        </TabsList>

        <TabsContent value="access" className="mt-4"><MasterAccess embedded /></TabsContent>
        <TabsContent value="backup" className="mt-4"><BackupRestore embedded /></TabsContent>
        <TabsContent value="chat"   className="mt-4"><Chat embedded /></TabsContent>
        <TabsContent value="recycle" className="mt-4"><RecycleBin embedded /></TabsContent>
      </Tabs>
    </div>
  );
}
